import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Download, RefreshCw } from "lucide-react";

import api from "../api";

function amount(value) {
  return `Rs. ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function downloadCsv(filename, headers, rows) {
  const csv = [
    headers.join(","),
    ...rows.map((row) =>
      row
        .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function currentMonthStart() {
  return `${new Date().toISOString().slice(0, 8)}01`;
}

function GSTReports() {
  const today = new Date().toISOString().slice(0, 10);
  const [activeTab, setActiveTab] = useState("gstr1");
  const [month, setMonth] = useState(currentMonth());
  const [nonGstRange, setNonGstRange] = useState({ date_from: currentMonthStart(), date_to: today });
  const [gstr1, setGstr1] = useState({ b2b: { invoices: [] }, b2c: { invoices: [] }, hsn_summary: [] });
  const [gstr3b, setGstr3b] = useState({ outward_supplies: {}, inward_itc: {}, net_tax_payable: {} });
  const [nonGst, setNonGst] = useState({ invoices: [], total_sales: 0, count: 0, total_paid: 0, total_balance: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadGstr1 = useCallback(async (selectedMonth = month) => {
    const res = await api.get(`/accounts/reports/gst/gstr1?month=${selectedMonth}`);
    setGstr1(res.data || {});
  }, [month]);

  const loadGstr3b = useCallback(async (selectedMonth = month) => {
    const res = await api.get(`/accounts/reports/gst/gstr3b-summary?month=${selectedMonth}`);
    setGstr3b(res.data || {});
  }, [month]);

  const loadNonGst = useCallback(async (range = nonGstRange) => {
    const params = new URLSearchParams(range);
    const res = await api.get(`/accounts/reports/non-gst-sales?${params.toString()}`);
    setNonGst(res.data || {});
  }, [nonGstRange]);

  const loadActive = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === "gstr1") {
        await loadGstr1(month);
      } else if (activeTab === "gstr3b") {
        await loadGstr3b(month);
      } else {
        await loadNonGst(nonGstRange);
      }
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load GST reports.");
    } finally {
      setLoading(false);
    }
  }, [activeTab, loadGstr1, loadGstr3b, loadNonGst, month, nonGstRange]);

  useEffect(() => {
    loadActive();
  }, [loadActive]);

  const gstr1ExportRows = useMemo(() => {
    const b2bRows = (gstr1.b2b?.invoices || []).map((row) => [
      "B2B",
      row.invoice_no,
      row.invoice_date,
      row.gstin,
      row.customer_name,
      row.supply_type,
      row.taxable_value,
      row.cgst,
      row.sgst,
      row.igst,
      row.total_amount,
    ]);
    const b2cRows = (gstr1.b2c?.invoices || []).map((row) => [
      "B2C",
      row.invoice_no,
      row.invoice_date,
      "",
      row.customer_name,
      row.supply_type,
      row.taxable_value,
      row.cgst,
      row.sgst,
      row.igst,
      row.total_amount,
    ]);
    const hsnRows = (gstr1.hsn_summary || []).map((row) => [
      "HSN",
      row.hsn_code,
      row.item_type,
      "",
      "",
      "",
      row.taxable_value,
      row.cgst,
      row.sgst,
      row.igst,
      row.quantity,
    ]);
    return [...b2bRows, ...b2cRows, ...hsnRows];
  }, [gstr1]);

  const nonGstExportRows = useMemo(
    () =>
      (nonGst.invoices || []).map((row) => [
        row.invoice_no,
        row.invoice_date,
        row.customer_name,
        row.total_amount,
        row.amount_paid,
        row.balance_due,
        row.status,
        row.payment_terms,
      ]),
    [nonGst]
  );

  const exportGstr1Csv = () => {
    downloadCsv(
      `gstr1-${gstr1.month || month}.csv`,
      ["Section", "Invoice No", "Date", "GSTIN", "Party", "Supply Type", "Taxable", "CGST", "SGST", "IGST", "Total/Qty"],
      gstr1ExportRows
    );
  };

  const exportNonGstCsv = () => {
    downloadCsv(
      `non-gst-sales-${nonGst.date_from || nonGstRange.date_from}-to-${nonGst.date_to || nonGstRange.date_to}.csv`,
      ["Invoice No", "Date", "Customer", "Total", "Paid", "Balance", "Status", "Payment Terms"],
      nonGstExportRows
    );
  };

  return (
    <div className="space-y-6">
      {error && <ErrorBanner message={error} />}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setActiveTab("gstr1")} className={`tab-btn ${activeTab === "gstr1" ? "tab-btn-active" : ""}`}>GSTR-1</button>
        <button onClick={() => setActiveTab("gstr3b")} className={`tab-btn ${activeTab === "gstr3b" ? "tab-btn-active" : ""}`}>GSTR-3B</button>
        <button onClick={() => setActiveTab("non-gst")} className={`tab-btn ${activeTab === "non-gst" ? "tab-btn-active" : ""}`}>Non-GST Sales</button>
      </div>

      {activeTab !== "non-gst" && (
        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
            <div>
              <h3 className="text-xl font-bold text-slate-900">{activeTab === "gstr1" ? "GSTR-1 Register" : "GSTR-3B Summary"}</h3>
              <p className="text-sm text-slate-500">Monthly GST filing data from approved and paid outward GST invoices.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input type="month" value={month} onChange={(event) => setMonth(event.target.value)} className="input" />
              <button onClick={loadActive} className="secondary-btn"><RefreshCw size={15} /> Generate</button>
              {activeTab === "gstr1" && <button onClick={exportGstr1Csv} className="secondary-btn"><Download size={15} /> Export CSV for Portal</button>}
            </div>
          </div>

          {activeTab === "gstr1" && (
            <div className="space-y-6 p-6">
              <div className="grid gap-4 md:grid-cols-3">
                <SummaryCard label="B2B Count" value={gstr1.b2b?.count || 0} plain />
                <SummaryCard label="B2B Taxable" value={amount(gstr1.b2b?.taxable_value)} plain />
                <SummaryCard label="B2C Count" value={gstr1.b2c?.count || 0} plain />
              </div>

              <TableSection
                title="B2B Invoices"
                loading={loading}
                headers={["Invoice No", "Date", "GSTIN", "Party", "Taxable", "CGST", "SGST", "IGST", "Total"]}
                rows={(gstr1.b2b?.invoices || []).map((row) => [
                  row.invoice_no,
                  row.invoice_date,
                  row.gstin,
                  row.customer_name,
                  amount(row.taxable_value),
                  amount(row.cgst),
                  amount(row.sgst),
                  amount(row.igst),
                  amount(row.total_amount),
                ])}
                emptyText="No B2B invoices for this month."
              />

              <TableSection
                title="B2C Invoices"
                loading={loading}
                headers={["Invoice No", "Date", "Party", "Supply Type", "Taxable", "Tax", "Total"]}
                rows={(gstr1.b2c?.invoices || []).map((row) => [
                  row.invoice_no,
                  row.invoice_date,
                  row.customer_name,
                  row.supply_type,
                  amount(row.taxable_value),
                  amount(row.total_tax),
                  amount(row.total_amount),
                ])}
                emptyText="No B2C invoices for this month."
              />

              <TableSection
                title="HSN Summary"
                loading={loading}
                headers={["HSN Code", "Item Type", "Quantity", "Taxable", "CGST", "SGST", "IGST", "Total Tax"]}
                rows={(gstr1.hsn_summary || []).map((row) => [
                  row.hsn_code,
                  row.item_type || "-",
                  row.quantity,
                  amount(row.taxable_value),
                  amount(row.cgst),
                  amount(row.sgst),
                  amount(row.igst),
                  amount(row.total_tax),
                ])}
                emptyText="No HSN data for this month."
              />
            </div>
          )}

          {activeTab === "gstr3b" && (
            <div className="grid gap-6 p-6 lg:grid-cols-3">
              <SummaryBlock
                title="Outward Supplies"
                rows={[
                  ["Taxable Value", amount(gstr3b.outward_supplies?.taxable_value)],
                  ["CGST", amount(gstr3b.outward_supplies?.cgst)],
                  ["SGST", amount(gstr3b.outward_supplies?.sgst)],
                  ["IGST", amount(gstr3b.outward_supplies?.igst)],
                  ["Total Tax", amount(gstr3b.outward_supplies?.total_tax)],
                ]}
              />
              <SummaryBlock
                title="Inward ITC"
                rows={[
                  ["CGST Credit", amount(gstr3b.inward_itc?.cgst_credit)],
                  ["SGST Credit", amount(gstr3b.inward_itc?.sgst_credit)],
                  ["IGST Credit", amount(gstr3b.inward_itc?.igst_credit)],
                  ["Total Credit", amount(gstr3b.inward_itc?.total_credit)],
                ]}
              />
              <SummaryBlock
                title="Net Tax Payable"
                rows={[
                  ["CGST", amount(gstr3b.net_tax_payable?.cgst)],
                  ["SGST", amount(gstr3b.net_tax_payable?.sgst)],
                  ["IGST", amount(gstr3b.net_tax_payable?.igst)],
                  ["Total", amount(gstr3b.net_tax_payable?.total)],
                ]}
              />
            </div>
          )}
        </section>
      )}

      {activeTab === "non-gst" && (
        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
            <div>
              <h3 className="text-xl font-bold text-slate-900">Non-GST Sales Report</h3>
              <p className="text-sm text-slate-500">Separate reporting for non-GST business with date range filtering and CSV export.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={nonGstRange.date_from}
                onChange={(event) => setNonGstRange((prev) => ({ ...prev, date_from: event.target.value }))}
                className="input"
              />
              <input
                type="date"
                value={nonGstRange.date_to}
                onChange={(event) => setNonGstRange((prev) => ({ ...prev, date_to: event.target.value }))}
                className="input"
              />
              <button onClick={loadActive} className="secondary-btn"><RefreshCw size={15} /> Generate</button>
              <button onClick={exportNonGstCsv} className="secondary-btn"><Download size={15} /> Export CSV</button>
            </div>
          </div>

          <div className="space-y-6 p-6">
            <div className="grid gap-4 md:grid-cols-4">
              <SummaryCard label="Invoice Count" value={nonGst.count || 0} plain />
              <SummaryCard label="Total Sales" value={amount(nonGst.total_sales)} plain />
              <SummaryCard label="Total Paid" value={amount(nonGst.total_paid)} plain />
              <SummaryCard label="Total Balance" value={amount(nonGst.total_balance)} plain />
            </div>

            <TableSection
              title="Non-GST Invoices"
              loading={loading}
              headers={["Invoice No", "Date", "Customer", "Total", "Paid", "Balance", "Status"]}
              rows={(nonGst.invoices || []).map((row) => [
                row.invoice_no,
                row.invoice_date,
                row.customer_name,
                amount(row.total_amount),
                amount(row.amount_paid),
                amount(row.balance_due),
                row.status,
              ])}
              emptyText="No non-GST invoices for the selected period."
            />
          </div>
        </section>
      )}

      <style>{`
        .input {
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .secondary-btn, .tab-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          border: 1px solid rgb(203 213 225);
          background: white;
          color: rgb(51 65 85);
        }
        .tab-btn-active {
          background: rgb(79 70 229);
          border-color: rgb(79 70 229);
          color: white;
        }
      `}</style>
    </div>
  );
}

function SummaryCard({ label, value, plain = false }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-2 font-bold text-slate-900 ${plain ? "text-2xl" : "text-sm"}`}>{value}</div>
    </div>
  );
}

function SummaryBlock({ title, rows }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <h4 className="mb-4 text-lg font-bold text-slate-900">{title}</h4>
      <div className="space-y-3">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between text-sm text-slate-700">
            <span>{label}</span>
            <span className="font-semibold">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TableSection({ title, headers, rows, loading, emptyText }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-6 py-5">
        <h4 className="text-lg font-bold text-slate-900">{title}</h4>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {headers.map((header) => (
                <th key={header} className="px-4 py-3">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && rows.map((row, index) => (
              <tr key={`${title}-${index}`} className="border-t border-slate-100">
                {row.map((cell, cellIndex) => (
                  <td key={`${title}-${index}-${cellIndex}`} className="px-4 py-3">{cell}</td>
                ))}
              </tr>
            ))}
            {!loading && !rows.length && (
              <tr>
                <td colSpan={headers.length} className="px-4 py-10 text-center text-slate-500">{emptyText}</td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={headers.length} className="px-4 py-10 text-center text-slate-500">Loading report...</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
      <AlertCircle size={16} />
      <span>{message}</span>
    </div>
  );
}

export default GSTReports;
